import { useCallback, useEffect, useRef, useState } from "react";

import { api, ApiError, type Me } from "./api";
import {
  beginSignIn,
  completeSignIn,
  forgetTokens,
  profileFrom,
  signOut,
  storedTokens,
  freshAccessToken,
} from "./auth";
import { connectRealtime, type RealtimeState } from "./realtime";
import { Garage } from "./screens/Garage";
import { JoinTeam } from "./screens/JoinTeam";
import { Records } from "./screens/Records";
import { Roster } from "./screens/Roster";
import { Televisions } from "./screens/Televisions";
import { Tournaments } from "./screens/Tournaments";
import { formatLocation, parseLocation, TABS, type Tab } from "./location";


/** Reads the restored screen out of the address bar. Parsing lives in `location.ts`, and is tested. */
function readLocation() {
  return parseLocation(window.location.hash);
}

function writeLocation(tab: Tab, garageClass: string) {
  const next = formatLocation({ tab, garageClass });
  if (window.location.hash !== next) {
    // `replaceState` rather than `pushState`: the tab bar is not a history stack, and making every tap a
    // back-button step would mean the phone's Back gesture walked through tabs instead of leaving the
    // app, which is not what anyone means by it.
    window.history.replaceState(null, "", window.location.pathname + next);
  }
}

/** Which team the person is looking at, remembered across reloads. */
const TEAM_KEY = "gej.team";


/**
 * Requirement 5.10's invite link, read from the path.
 *
 * The app is a single page served for every route by CloudFront, so `/join/<code>` arrives here rather
 * than at a router. Read once at module load and **removed from the URL immediately**: the code is a
 * capability (5.13), and leaving it in the address bar puts it in screenshots, in shared tabs, and in
 * the browser history of a phone that gets handed round.
 */
function takeInviteCode(): string | null {
  const match = /^\/join\/(.+)$/.exec(window.location.pathname);
  if (!match) return null;
  const code = decodeURIComponent(match[1] ?? "");
  window.history.replaceState(null, "", "/" + window.location.hash);
  return code || null;
}

export function App() {
  const [signedIn, setSignedIn] = useState(() => storedTokens() !== null);
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);
  // Restored from the hash, so a reload comes back to the screen the family was on. See `readLocation`.
  const [tab, setTab] = useState<Tab>(() => readLocation().tab);
  const [garageClass, setGarageClass] = useState(() => readLocation().garageClass);
  // Captured before the first render can rewrite the URL, so a reload during sign-in does not lose it.
  const [inviteCode, setInviteCode] = useState<string | null>(takeInviteCode);
  const [chosenTeam, setChosenTeam] = useState<string | null>(() => localStorage.getItem(TEAM_KEY));

  // Bumped by any realtime event. Screens take it as a dependency, which turns "something changed on
  // the server" into a re-read without any screen having to know what changed — design §5.3's rule
  // applied to the UI layer.
  const [revision, setRevision] = useState(0);
  // The garage gets its own, and this is a cost decision rather than tidiness. Listing a garage mints a
  // CloudFront signature per image, making it the most expensive read in the app, and the shared
  // `revision` bumps on *every* event — so calling a heat on the television made the phone re-read the
  // whole garage, repeatedly, throughout a tournament. Only vehicle events can change a garage.
  const [garageRevision, setGarageRevision] = useState(0);
  const [link, setLink] = useState<RealtimeState>("connecting");

  // Keep the hash in step with the screen, so a reload or a restored background tab comes back here.
  //
  // Gated on `busy` so nothing touches the address bar until the bootstrap below has finished with it.
  // `completeSignIn` no longer depends on that — it captures the callback at module load — but this
  // effect is declared before that one and runs first, and an effect that rewrites the URL out from
  // under a sign-in in progress is a trap worth closing at both ends rather than one.
  useEffect(() => {
    if (busy) return;
    writeLocation(tab, garageClass);
  }, [tab, garageClass, busy]);

  // And honour the hash changing underneath us — the phone's Back gesture, or a link someone pasted.
  useEffect(() => {
    const onHashChange = () => {
      const next = readLocation();
      setTab(next.tab);
      setGarageClass(next.garageClass);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Completes the hosted-UI redirect if this load is one, and is a no-op otherwise.
        const fresh = await completeSignIn();
        if (fresh) setSignedIn(true);
        if (!fresh && storedTokens() === null) {
          if (!cancelled) setBusy(false);
          return;
        }
        // `GET /me` provisions a personal team on first call (requirement 5.3), so this doubles as the
        // last step of signing up.
        const profile = await api.me();
        if (!cancelled) {
          setMe(profile);
          setSignedIn(true);
        }
      } catch (cause) {
        if (cancelled) return;
        if (cause instanceof ApiError && cause.isAuthFailure) {
          // The token is not usable, and refreshing cannot help: a refresh keeps the scopes the token
          // was issued with, so one minted before a scope was added stays broken forever. Discard it
          // and offer sign-in, which is a single tap while the Cognito session cookie lasts.
          forgetTokens();
          setSignedIn(false);
          setError("Your sign-in needs renewing. Please sign in again.");
        } else {
          setError(cause instanceof Error ? cause.message : "Something went wrong.");
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Which team is on screen. Requirement 5.12 caps a person at one family team plus their personal
   * one, so this is a choice between at most two — but it has to be a choice, and it has to survive a
   * reload. This replaced `me.teams[0]`, which was correct only while there was exactly one way to
   * have a team.
   *
   * Prefers the remembered team, then the first non-personal one: after joining a family, that family
   * is what you opened the app for, and the personal team is a private corner rather than a home.
   */
  const team =
    me?.teams.find((entry) => entry.teamId === chosenTeam) ??
    me?.teams.find((entry) => !entry.personal) ??
    me?.teams[0];
  const teamId = team?.teamId;
  const teamName = team?.name ?? "your team";

  const pickTeam = useCallback((next: string) => {
    localStorage.setItem(TEAM_KEY, next);
    setChosenTeam(next);
    setError(null);
  }, []);

  const reloadMe = useCallback(() => {
    void api
      .me()
      .then(setMe)
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Something went wrong."));
  }, []);

  // One socket for the whole session rather than one per screen, so switching tabs does not
  // reconnect. Requirement 14.2.
  const bump = useCallback((type: string) => {
    setRevision((value) => value + 1);
    if (type.startsWith("vehicle.") || type.startsWith("garage.")) {
      setGarageRevision((value) => value + 1);
    }
  }, []);
  const bumpRef = useRef(bump);
  bumpRef.current = bump;

  useEffect(() => {
    if (!teamId) return;
    let stop: (() => void) | undefined;
    let cancelled = false;
    (async () => {
      const token = await freshAccessToken();
      if (!token || cancelled) return;
      stop = connectRealtime({
        token,
        teamId,
        onEvent: (event) => bumpRef.current(event.type),
        onState: setLink,
      });
    })();
    return () => {
      cancelled = true;
      stop?.();
    };
  }, [teamId]);

  if (busy) {
    return (
      <div className="app">
        <div className="centre">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (!signedIn || !me) {
    return <SignIn error={error} invited={inviteCode !== null} />;
  }

  // Requirement 5.11 — before any tab, before any team. Following a link has to be answerable, and
  // this is deliberately ahead of the `!teamId` branch below: an invitation is a perfectly good
  // answer to having nowhere to be.
  if (inviteCode) {
    return (
      <div className="app">
        <header className="top">
          <h1>
            <span className="wordmark">JAM</span> Invitation
          </h1>
        </header>
        <main>
          <JoinTeam
            code={inviteCode}
            onJoined={(joinedTeam) => {
              pickTeam(joinedTeam);
              setInviteCode(null);
              setTab("races");
              reloadMe();
            }}
            onCancel={() => setInviteCode(null)}
          />
        </main>
      </div>
    );
  }

  if (!teamId) {
    return (
      <div className="app">
        <div className="centre">
          <h2>No team yet</h2>
          <p className="muted">
            A team is created for you the first time you sign in. Reload to try again.
          </p>
          <button className="btn primary" onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="top">
        <h1>
          <span className="wordmark">JAM</span> {teamName}
        </h1>
        {/* A dot, not a label. Connection state is not something a family should have to read. */}
        <span
          className={`link-dot${link === "live" ? " live" : ""}`}
          title={link === "live" ? "Live" : "Reconnecting"}
        />
      </header>

      <main>
        {error && <div className="banner error">{error}</div>}
        {tab === "races" && (
          <Tournaments teamId={teamId} mySub={me.sub} revision={revision} onError={setError} />
        )}
        {tab === "garage" && (
          <Garage
            teamId={teamId}
            revision={garageRevision}
            onError={setError}
            showing={garageClass}
            onShowing={setGarageClass}
          />
        )}
        {tab === "records" && <Records teamId={teamId} revision={revision} onError={setError} />}
        {tab === "tv" && <Televisions teamId={teamId} revision={revision} onError={setError} />}
        {tab === "team" && (
          <Roster
            teamId={teamId}
            me={me}
            revision={revision}
            onError={setError}
            onSignOut={signOut}
            onTeamsChanged={reloadMe}
            onPickTeam={pickTeam}
          />
        )}
      </main>

      <nav className="tabs">
        {TABS.map((entry) => (
          <button
            key={entry.id}
            aria-current={tab === entry.id}
            onClick={() => {
              setError(null);
              setTab(entry.id);
            }}
          >
            <span className="glyph" aria-hidden="true">
              {entry.glyph}
            </span>
            {entry.label}
          </button>
        ))}
      </nav>
    </div>
  );
}

/**
 * The entry screen. Requirement 5.2, task 17.2.
 *
 * The one auth surface this app fully owns, which is why it carries the identity rather than deferring
 * to Cognito's page for it: the branding in `infra/managed-login` makes the hosted pages match *this*,
 * not the other way round.
 *
 * **One action, not two.** This offered "Sign in" and "Create an account" as separate buttons going to
 * `/login` and `/signup`. That was a distinction without a difference for a family — Cognito's sign-in
 * page links to sign-up itself, so the second button led somewhere one tap away — and it made the first
 * screen a decision instead of a door. Signing up is a link under the button now, in the same place the
 * hosted page puts it.
 */
function SignIn({ error, invited }: { error: string | null; invited: boolean }) {
  const stored = storedTokens();
  const profile = stored ? profileFrom(stored) : null;
  return (
    <div className="app">
      <div className="centre gate">
        <h2 className="gate-title">
          Grayson Earl <span className="wordmark">JAM</span>
        </h2>
        {/*
          Requirement 5.11, one step earlier than the confirmation. Somebody arriving from an invite
          link was sent here by a relative rather than by curiosity, and telling them why they are
          looking at a sign-in screen is the difference between finishing and closing the tab.
        */}
        <p className="muted gate-blurb">
          {invited
            ? "You've been invited to a team. Sign in first, and we'll ask before joining you to anything."
            : "Run the bracket from your phone while the television shows the races."}
        </p>
        {error && <div className="banner error">{error}</div>}
        <button className="btn primary wide" onClick={() => void beginSignIn("login")}>
          Sign in
        </button>
        <button className="btn link-button" onClick={() => void beginSignIn("signup")}>
          New here? Create an account
        </button>
        {profile?.email && <p className="muted gate-last">Last signed in as {profile.email}</p>}
      </div>
    </div>
  );
}
